/**
 * Audit Scanning Card Controller: bottom-pinned
 * mobile progress card.
 *
 * Listens on `document` for `audit-stage:transition`
 * events dispatched (with `bubbles: true`) by the
 * sibling `audit-stage` controller. Each transition
 * carries the new `currentKey`; we compare against
 * our local view of the step we're showing and, when
 * it changes, swap the headline + progress bar fill
 * atomically.
 *
 * State this controller owns:
 *
 * - `currentStepKey`: the step the card is currently
 *   showing. Starts at `initialStepKeyValue` (server-
 *   computed via `RestaurantAudit#initial_stage_key`).
 * - `completedSteps`: number of `done` steps. Starts
 *   at `completedStepsValue`. Incremented locally on
 *   each transition we accept; capped at total steps.
 *
 * The audit flow is click-driven (the Continue button
 * gates on `audit-step:complete`), not time-based, so
 * there is intentionally no countdown: a ticker would
 * either lie or stall at zero while the user lingers.
 *
 * `prefers-reduced-motion`: the spinner uses
 * `motion-safe:animate-spin` so it stops on its own.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    totalSteps: { type: Number, default: 6 },
    stepHeadlines: { type: Object, default: {} },
    websiteHost: { type: String, default: "" },
    initialStepKey: { type: String, default: "" },
    completedSteps: { type: Number, default: 0 },
  };

  static targets = ["progressBar", "headline"];

  connect() {
    this.currentStepKey =
      this.initialStepKeyValue || "locating";
    this.completedSteps =
      Number(this.completedStepsValue) || 0;

    this.boundHandleTransition =
      this.handleTransition.bind(this);
    document.addEventListener(
      "audit-stage:transition",
      this.boundHandleTransition
    );

    this.renderHeadline();
    this.renderProgressBar();
  }

  disconnect() {
    if (this.boundHandleTransition) {
      document.removeEventListener(
        "audit-stage:transition",
        this.boundHandleTransition
      );
    }
  }

  // -------- transition handling --------

  handleTransition(event) {
    const nextKey = event.detail?.currentKey;
    if (!nextKey) return;
    if (nextKey === this.currentStepKey) return;

    this.currentStepKey = nextKey;
    if (this.completedSteps < this.totalStepsValue) {
      this.completedSteps += 1;
    }

    this.renderHeadline();
    this.renderProgressBar();
  }

  // -------- rendering --------

  renderHeadline() {
    if (!this.hasHeadlineTarget) return;
    this.headlineTarget.textContent =
      this.headlineFor(this.currentStepKey);
  }

  renderProgressBar() {
    if (!this.hasProgressBarTarget) return;
    const total = this.totalStepsValue || 1;
    const pct = Math.min(
      100,
      Math.round(
        (this.completedSteps / total) * 100
      )
    );
    this.progressBarTarget.style.width = `${pct}%`;
  }

  headlineFor(key) {
    const map = this.stepHeadlinesValue || {};
    if (key === "sampling_brand") {
      const host = this.websiteHostValue;
      if (!host) {
        return map.sampling_brand_no_host || "";
      }
      const tmpl = map.sampling_brand || "";
      return tmpl.replace(
        "%{website_host}",
        host
      );
    }
    return map[key] || "";
  }
}
